const LAST_ACTIVITY_KEY = "session_last_activity_at";
const SESSION_IDLE_TIMEOUT_MS_KEY = "session_idle_timeout_ms";

function getEnvNumber(key: string): number | null {
  const raw = (process.env as any)?.[key];
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function getConfiguredIdleTimeoutMs(): number | null {
  const ms = getEnvNumber("REACT_APP_IDLE_TIMEOUT_MS");
  if (ms !== null && ms > 0) return ms;

  const minutes = getEnvNumber("REACT_APP_IDLE_TIMEOUT_MINUTES");
  if (minutes !== null && minutes > 0) return minutes * 60_000;

  return null;
}

function getBaseIdleTimeoutMs(): number {
  return getConfiguredIdleTimeoutMs() ?? 30 * 60_000;
}

/**
 * Idle timeout for logout:
 * - env value when configured
 * - otherwise default 30 minutes
 * Backend stored value can only make timeout shorter, never longer.
 */
export function getIdleTimeoutMs(): number {
  const baseMs = getBaseIdleTimeoutMs();

  const storedMs = localStorage.getItem(SESSION_IDLE_TIMEOUT_MS_KEY);
  if (storedMs) {
    const ms = Number(storedMs);
    if (Number.isFinite(ms) && ms > 0) return Math.min(ms, baseMs);
  }

  return baseMs;
}

/**
 * Store idle timeout from backend login response. Call when storing access_token.
 * expires_in = seconds until token expires; we use the same duration for idle logout.
 */
export function setSessionIdleTimeoutFromExpiresIn(expiresInSeconds: number): void {
  try {
    const backendMs = Number(expiresInSeconds) * 1000;
    if (Number.isFinite(backendMs) && backendMs > 0) {
      // Backend value can only tighten, not loosen, configured/default timeout.
      const effectiveMs = Math.min(backendMs, getBaseIdleTimeoutMs());

      localStorage.setItem(SESSION_IDLE_TIMEOUT_MS_KEY, String(effectiveMs));
    }
  } catch (_) {
    // ignore storage failures
  }
}

export function clearSessionIdleTimeout(): void {
  try {
    localStorage.removeItem(SESSION_IDLE_TIMEOUT_MS_KEY);
  } catch (_) {
    // ignore
  }
}

export function getLastActivityAt(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function markUserActivity(now: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    window.dispatchEvent(new Event("session-activity"));
  } catch (_) {
    // ignore storage failures (private mode, quota, etc.)
  }
}

export function isIdleTimedOut(now: number = Date.now()): boolean {
  const last = getLastActivityAt();
  if (!last) return false;
  return now - last > getIdleTimeoutMs();
}

